import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Modal,
  Form,
  Input,
  Card,
  Empty,
  Spin,
  message,
  Typography,
  Row,
  Col,
  Tag,
  Tooltip,
  Switch,
} from 'antd';
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  RobotOutlined,
  MenuUnfoldOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { useMyContext } from 'Context/MyContextProvider';
import Flex from 'components/shared-components/Flex';
import {
  getChatbotGroups,
  createChatbotGroup,
  updateChatbotGroup,
  deleteChatbotGroup,
  updateChatbotGroupStatus,
} from 'services/chatbotFlowService';
import './styles/flowBuilder.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const FlowGroupList = () => {
  const { UserData, Permisson } = useMyContext();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!Permisson?.includes('View Chatbot')) {
      navigate('404');
    }
  }, [Permisson, navigate]);

  const fetchGroups = async () => {
    try {
      setGroupsLoading(true);
      const res = await getChatbotGroups(UserData?.id);
      setGroups(res?.data || []);
    } catch {
      message.error('Failed to load chatbot groups');
    } finally {
      setGroupsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [UserData?.id]);

  useEffect(() => {
    const active = groups.find((g) => g.status === 1 || g.status === true);
    if (active) setActiveGroupId(active.id);
  }, [groups]);

  const handleAddGroup = () => {
    setCurrentGroup(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditGroup = (group) => {
    setCurrentGroup(group);
    form.setFieldsValue({ name: group.name, description: group.description });
    setModalVisible(true);
  };

  const handleSaveGroup = async (values) => {
    try {
      setGroupsLoading(true);
      const payload = { ...values, user_id: UserData?.id, chatbot_ids: [] };
      if (currentGroup) {
        await updateChatbotGroup(currentGroup.id, payload);
        message.success('Group updated');
      } else {
        await createChatbotGroup(payload);
        message.success('Group created');
      }
      setModalVisible(false);
      fetchGroups();
    } catch {
      message.error('Failed to save group');
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      setGroupsLoading(true);
      await deleteChatbotGroup(groupId);
      message.success('Group deleted');
      fetchGroups();
    } catch {
      message.error('Failed to delete group');
    } finally {
      setGroupsLoading(false);
    }
  };

  const updateGroupStatus = async (groupId, status) => {
    try {
      setStatusUpdateLoading(true);
      await updateChatbotGroupStatus({
        group_id: groupId,
        user_id: UserData?.id,
        status,
      });
      message.success(`Group ${status ? 'activated' : 'deactivated'}`);
      setActiveGroupId(status ? groupId : null);
      fetchGroups();
    } catch {
      message.error('Failed to update status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const openFlows = (group) => {
    const slug = encodeURIComponent(group.name || 'group');
    navigate(`/chatbot-flows/${group.id}/${slug}`);
  };

  if (groupsLoading && groups.length === 0) {
    return (
      <Flex justifyContent="center" alignItems="center" style={{ minHeight: '60vh' }}>
        <Spin size="large" tip="Loading..." />
      </Flex>
    );
  }

  if (groups.length === 0) {
    return (
      <Card>
        <Empty description="No Chatbot Groups Found" />
        <Flex justifyContent="center" style={{ marginTop: 16 }}>
          <Button type="primary" icon={<PlusCircleOutlined />} onClick={handleAddGroup}>
            Create Chatbot Group
          </Button>
        </Flex>
        <GroupModal
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          form={form}
          currentGroup={currentGroup}
          onFinish={handleSaveGroup}
        />
      </Card>
    );
  }

  return (
    <div>
      <Row gutter={16}>
        {groups.map((group) => (
          <Col key={group.id} xs={24} sm={12} md={8} lg={8}>
            <Card hoverable className="flow-group-card">
              <Flex align="center" justifyContent="space-between">
                <Title level={4} style={{ margin: 0 }}>
                  {group.name}
                </Title>
                <div className="d-flex align-items-center gap-2">
                  <Switch
                    checked={activeGroupId === group.id}
                    onChange={(checked) => updateGroupStatus(group.id, checked)}
                    loading={statusUpdateLoading && activeGroupId === group.id}
                    size="small"
                  />
                  <Tag color="blue" icon={<RobotOutlined />}>
                    {group?.chatbots?.length || 0} Requests
                  </Tag>
                </div>
              </Flex>
              <Text type="secondary">{group.description}</Text>
              <div className="d-flex justify-content-end mt-3 pt-3 border-top gap-2">
                <Tooltip title="View v2 flows">
                  <Button
                    icon={<ApartmentOutlined />}
                    size="small"
                    shape="circle"
                    onClick={() => openFlows(group)}
                  />
                </Tooltip>
                <Tooltip title="Legacy chatbots">
                  <Button
                    icon={<MenuUnfoldOutlined />}
                    size="small"
                    className="border"
                    shape="circle"
                    onClick={() =>
                      navigate(
                        `/manage-request/${group.id}/${encodeURIComponent(group.name || 'group')}`
                      )
                    }
                  />
                </Tooltip>
                <Tooltip title="Edit group">
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    size="small"
                    shape="circle"
                    onClick={() => handleEditGroup(group)}
                  />
                </Tooltip>
                <Tooltip title="Delete group">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    shape="circle"
                    onClick={() =>
                      Modal.confirm({
                        title: 'Delete this group?',
                        onOk: () => handleDeleteGroup(group.id),
                      })
                    }
                  />
                </Tooltip>
              </div>
            </Card>
          </Col>
        ))}
        <Col xs={24} sm={12} md={8} lg={3}>
          <Card hoverable className="flow-group-add-card" onClick={handleAddGroup}>
            <Button
              className="border-0"
              shape="circle"
              icon={<PlusCircleOutlined style={{ fontSize: 40 }} />}
              size="large"
            />
          </Card>
        </Col>
      </Row>
      <GroupModal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        form={form}
        currentGroup={currentGroup}
        onFinish={handleSaveGroup}
      />
    </div>
  );
};

function GroupModal({ open, onCancel, form, currentGroup, onFinish }) {
  return (
    <Modal
      title={currentGroup ? 'Edit Chatbot Group' : 'Create Chatbot Group'}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={520}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item name="name" label="Group Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Product commerce" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
          <Button onClick={onCancel} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            {currentGroup ? 'Update' : 'Create'}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default FlowGroupList;
